import type { HTMLAttributes, ReactNode } from 'react'
import styles from './FilterBar.module.css'

export interface FilterBarProps extends HTMLAttributes<HTMLDivElement> {
  /** Primary axis — pinned to the start of the top row. */
  primary?: ReactNode
  /** Toggles and search — pinned to the end of the top row. */
  actions?: ReactNode
  /** Secondary filters, e.g. game pills. They get their own row and wrap there. */
  children?: ReactNode
  /** Stick the bar under the app chrome as the results scroll past it. */
  sticky?: boolean
  /** Distance from the top of the viewport when `sticky`, in px. Defaults to 0. */
  offset?: number
}

/**
 * The filter row for a discovery page.
 *
 * Two fixed rows rather than one wrapping line, because the number of
 * secondary filters changes with the primary one — picking "Universities"
 * drops the game list from seven pills to one. As a single wrapping flex row
 * that re-flowed everything: the search field and toggles jumped between lines
 * on every facet change, so the control you just clicked moved out from under
 * the pointer. Keeping the primary axis and the actions on their own row means
 * only the pills themselves grow and shrink, and nothing above them moves.
 *
 * Bounded by hairlines rather than floated as a glass slab: filters are chrome,
 * not content.
 */
export function FilterBar({
  primary,
  actions,
  children,
  sticky = false,
  offset = 0,
  className,
  style,
  ...props
}: FilterBarProps) {
  const classes = [styles.bar, sticky && styles.sticky, className].filter(Boolean).join(' ')
  return (
    <div className={classes} style={sticky ? { ...style, top: offset } : style} {...props}>
      {(primary ?? actions) && (
        <div className={styles.top}>
          <div className={styles.primary}>{primary}</div>
          {actions && <div className={styles.actions}>{actions}</div>}
        </div>
      )}
      {children && <div className={styles.secondary}>{children}</div>}
    </div>
  )
}

export interface FilterOption {
  value: string
  label: string
  /** Trailing facet total, e.g. `Leagues 14`. */
  count?: number
  /** Leading mark — a game or org logo. */
  icon?: ReactNode
}

export interface FilterOptionsProps {
  /** Accessible name for the group, e.g. "Game" or "Competition type". */
  label: string
  options: FilterOption[]
  /** `null` means the leading `allLabel` choice is active. */
  value: string | null
  onChange: (value: string | null) => void
  /**
   * When given, a leading choice is rendered that selects `null` — the
   * "no filter" state. Omit it when the options already carry their own.
   */
  allLabel?: string
  allCount?: number
  /**
   * `segment` is an underlined text run for the primary axis of the page
   * (league vs tournament, club vs university). `pill` is a bordered chip for
   * secondary axes such as game.
   */
  variant?: 'segment' | 'pill'
  disabled?: boolean
}

/** Single-select filter group. */
export function FilterOptions({
  label,
  options,
  value,
  onChange,
  allLabel,
  allCount,
  variant = 'pill',
  disabled = false,
}: FilterOptionsProps) {
  const all: FilterOption[] = allLabel
    ? [{ value: '', label: allLabel, ...(allCount != null ? { count: allCount } : {}) }]
    : []

  return (
    <div className={`${styles.group} ${styles[variant]}`} role="group" aria-label={label}>
      {[...all, ...options].map((option) => {
        // The synthetic "all" entry carries an empty value and maps to null, so
        // callers keep a plain `string | null` in state.
        const isAll = allLabel != null && option.value === ''
        const active = isAll ? value === null : value === option.value
        return (
          <button
            key={option.value || '__all__'}
            type="button"
            className={`${styles.option} ${active ? styles.active : ''}`}
            aria-pressed={active}
            disabled={disabled}
            onClick={() => { onChange(isAll ? null : option.value) }}
          >
            {option.icon && <span className={styles.optionIcon}>{option.icon}</span>}
            <span>{option.label}</span>
            {/* The space is for screen readers: adjacent inline spans join with
                none, so the name read "All48". Flex layout doesn't render it. */}
            {option.count != null && <>{' '}<span className={styles.count}>{option.count}</span></>}
          </button>
        )
      })}
    </div>
  )
}

export interface FilterToggleProps {
  pressed: boolean
  onChange: (pressed: boolean) => void
  children: ReactNode
  disabled?: boolean
}

/** On/off filter, e.g. "Cash prizes" or "Recruiting only". */
export function FilterToggle({ pressed, onChange, children, disabled = false }: FilterToggleProps) {
  return (
    <button
      type="button"
      className={`${styles.option} ${styles.standalone} ${pressed ? styles.active : ''}`}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={() => { onChange(!pressed) }}
    >
      {children}
    </button>
  )
}
